import { useState } from "react";
import axios from "axios";
import { format, addDays } from "date-fns";
import { ro, enUS } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { Reveal, Eyebrow, SectionTitle, EASE } from "./Reveal";
import { StepSystems, StepProperty, StepDate, StepContact } from "./BookingSteps";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { services: [], property_type: "", area: "", date: null, slot: "", name: "", phone: "", email: "", city: "", address: "", notes: "", consent: false };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validators = [
  (f) => f.services.length > 0 || "services",
  (f) => !!(f.property_type && f.area) || "property",
  (f) => !!(f.date && f.slot) || "date",
  (f) => !!(f.name.trim().length >= 2 && /^[0-9+\s().-]{6,20}$/.test(f.phone.trim()) && EMAIL_RE.test(f.email.trim()) && f.city.trim().length >= 2 && f.consent) || "contact",
];

const Progress = ({ step, labels }) => (
  <div className="grid grid-cols-4 gap-2" data-testid="booking-progress">
    {labels.map((l, i) => (
      <div key={l}>
        <div className="h-[3px] bg-white/10 overflow-hidden">
          <motion.div className="h-full bg-red-600" initial={false} animate={{ width: i <= step ? "100%" : "0%" }} transition={{ duration: 0.6, ease: EASE }} />
        </div>
        <p className={`mt-2 font-mono text-[11px] uppercase tracking-widest ${i <= step ? "text-white" : "text-zinc-500"}`}><span className="text-red-500">0{i + 1}</span> <span className="hidden sm:inline">{l}</span></p>
      </div>
    ))}
  </div>
);

export const Booking = () => {
  const { t, lang } = useLang();
  const b = t.booking;
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setError(""); };
  const locale = lang === "ro" ? ro : enUS;

  const next = () => {
    const ok = validators[step](form);
    if (ok !== true) return setError(b.errors[ok]);
    if (step < 3) setStep(step + 1);
    else submit();
  };

  const submit = async () => {
    setSending(true);
    try {
      const payload = { ...form, date: format(form.date, "yyyy-MM-dd"), lang, name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim(), city: form.city.trim() };
      const res = await axios.post(`${API}/bookings`, payload);
      const ref = res.data?.reference;
      if (!ref) throw new Error("no ref");
      navigate(`/confirmare/${ref}`);
    } catch (e) {
      setError(b.errors.server);
      toast.error(b.errors.server);
    } finally {
      setSending(false);
    }
  };

  const steps = [
    <StepSystems key="s" form={form} set={set} />,
    <StepProperty key="p" form={form} set={set} />,
    <StepDate key="d" form={form} set={set} locale={locale} minDate={addDays(new Date(), 1)} maxDate={addDays(new Date(), 120)} />,
    <StepContact key="c" form={form} set={set} />,
  ];

  return (
    <section id="booking" data-testid="booking-section" className="relative py-28 lg:py-40 overflow-hidden">
      <div className="absolute inset-0 tac-grid opacity-60" />
      <div className="absolute right-[-200px] top-40 h-[520px] w-[520px] rounded-full bg-red-800/20 blur-[140px]" />
      <div className="relative max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4">
          <Eyebrow>{b.eyebrow}</Eyebrow>
          <SectionTitle className="mt-6">{b.title}</SectionTitle>
          <p className="mt-6 text-zinc-300 leading-relaxed">{b.sub}</p>
          <span className="mt-8 inline-flex items-center gap-2 border border-red-600/50 bg-red-950/30 px-4 py-2 font-mono text-xs uppercase tracking-widest text-red-300" data-testid="booking-free-badge"><span className="h-1.5 w-1.5 bg-red-500 rounded-full blink" />{b.badge}</span>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-8">
          <div className="relative border border-white/10 bg-black/60 backdrop-blur-xl p-6 sm:p-10" data-testid="booking-form">
            <div className="absolute top-0 left-0 h-px w-1/3 bg-gradient-to-r from-red-600 to-transparent" />
            <Progress step={step} labels={b.steps} />
            <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, ease: EASE }} className="mt-10 min-h-[340px]">
              {steps[step]}
            </motion.div>
            {error && <p data-testid="booking-error" className="mt-6 text-sm text-red-400">{error}</p>}
            <div className="mt-8 flex items-center justify-between gap-4 border-t border-white/10 pt-6">
              <button data-testid="booking-back-button" disabled={step === 0 || sending} onClick={() => { setError(""); setStep(step - 1); }} className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-300 hover:text-white disabled:opacity-30 transition-opacity"><ArrowLeft className="h-4 w-4" />{b.back}</button>
              <button data-testid={step < 3 ? `booking-step-${step + 1}-next` : "booking-submit-button"} disabled={sending} onClick={next} className="group clip-btn flex items-center gap-3 bg-red-600 hover:bg-red-500 disabled:opacity-60 transition-colors px-6 sm:px-8 py-4 font-mono text-xs uppercase tracking-[0.18em] text-white">
                {sending ? <><Loader2 className="h-4 w-4 animate-spin" />{b.sending}</> : <>{step < 3 ? b.next : b.submit}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>}
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
